// web/src/screens/admin/attempts_detail.ts
//
// One attempt: outcome + cost/latency telemetry, the resolved v3 task/card/
// action context, the STT/LLM detail (with collapsible per-call payload
// panels), and the captured audio player when retention is enabled.
//
// v2-aligned rebuild of v1's attempt_detail.gohtml + llm_calls.gohtml +
// stt_call.gohtml — folded into one screen (the per-call lists are inline
// panels rather than separate routes; same data, less plumbing). v1's
// flat card fields (word_text / hint_lv / reveal_phrase_lv / first_phoneme_*
// / practice_phrase_lv) and v1's attempt.phase/praise_key are gone in the
// v2 schema and are not shown.
//
// All API-supplied text via textContent only (T-11-09a).

import { renderAdminShell } from "./admin_shell.js";
import { adminGet, AdminError } from "../../api/admin.js";
import type {
  AdminAttemptDetailDTO, AdminLLMCallDTO, AdminSTTCallDTO, AdminRouteProps,
} from "../../types/api.js";
import {
  fmtMicrousd, fmtTimestamp, kvList, code, section, adminLink, errMessage, evalColors, pill,
} from "./logs_common.js";

export function mount(root: HTMLElement, props?: unknown): () => void {
  const p = props as AdminRouteProps | undefined;
  const attemptID = p && p.kind === "admin-attempt-detail" ? p.attemptID : "";

  const main = renderAdminShell(root, [
    { label: "Game log", href: "/admin/sessions" },
    { label: "Attempt" },
    { label: attemptID || "?" },
  ]);

  const status = document.createElement("p");
  status.textContent = "Loading…";
  status.setAttribute("style", "color: #6b7280; font-size: 14px;");
  main.appendChild(status);

  const body = document.createElement("div");
  main.appendChild(body);

  function render(a: AdminAttemptDetailDTO): void {
    status.remove();
    body.replaceChildren();

    // "← session" backlink (the breadcrumb can't carry the session id without
    // a fetch; add it here once we have the DTO).
    const back = document.createElement("p");
    back.setAttribute("style", "margin: 0 0 12px 0; font-size: 14px;");
    back.appendChild(adminLink("← back to session", `/admin/sessions/${encodeURIComponent(a.session_id)}`));
    body.appendChild(back);

    // ── Outcome ───────────────────────────────────────────────────────
    const ev = evalColors(a.evaluation);
    section(body, `Attempt #${a.attempt_number} — outcome`).appendChild(kvList([
      ["Evaluation", pill(a.evaluation, ev.fg, ev.bg)],
      ["Guard reason", a.guard_reason ? code(a.guard_reason) : "— (LLM was called)"],
      ["Created", fmtTimestamp(a.created_at)],
      ["Total cost", code(fmtMicrousd(a.total_cost_microusd))],
      ["STT / LLM / TTS latency", `${a.stt_latency_ms} / ${a.llm_latency_ms} / ${a.tts_latency_ms} ms`],
      ["Action taken (into this turn)", a.action_taken_slot ? code(a.action_taken_slot) : "— (first turn)"],
      ["Decision action (this turn)", a.decision_action
        ? code(`${a.decision_action.slot} → ${a.decision_action.action_type}`)
        : (a.decision_action_slot ? code(a.decision_action_slot) : "— (game complete)")],
      ["Decision next task", a.decision_next_task_id ? code(a.decision_next_task_id) : "— (game complete)"],
    ]));

    // ── Performance (latency) ─────────────────────────────────────────────
    // Phase 2 perf tracking. server_total_ms is set on the attempt insert when
    // tracking is on; the client_* marks arrive via the post-turn beacon (null
    // if tracking was off, the turn was silent/aborted, or the beacon was lost).
    const srvMs = (v: number | null): string => (v != null ? `${v} ms` : "—");
    const cliMs = (v: number | null): string => (v != null ? `${v} ms` : "— (no client report)");
    section(body, "Performance (latency)").appendChild(kvList([
      ["Server total", srvMs(a.server_total_ms)],
      ["Client round-trip", cliMs(a.client_roundtrip_ms)],
      ["Network (round-trip − server)", cliMs(a.client_network_ms)],
      ["Audio first sound", cliMs(a.client_audio_first_sound_ms)],
      ["Perceived (incl. 2s silence hold)", cliMs(a.client_perceived_ms)],
      ["Pipeline (excl. silence hold)", cliMs(a.client_pipeline_ms)],
    ]));

    // ── Card / Task context ───────────────────────────────────────────
    const cardBody = section(body, "Card / task context");
    cardBody.appendChild(kvList([
      ["Card", `${a.card_short_code} · ordinal ${a.card_ordinal}`],
      ["Task", code(a.task_short_code)],
      ["Displayed text", a.displayed_text ?? "(image-only)"],
      ["Correct answer", a.correct_answer ?? "(none)"],
      ["Alt accepted", (a.alt_accepted_answers ?? []).join(", ") || "(none)"],
      ["Game", `${a.game_short_code}`],
    ]));
    if (a.task_image_path) {
      const imgWrap = document.createElement("div");
      imgWrap.setAttribute("style", "margin-top: 8px;");
      const pathLine = document.createElement("div");
      pathLine.setAttribute("style", "font-size: 12px; color: #6b7280; margin-bottom: 4px;");
      pathLine.append("Image: ");
      pathLine.appendChild(code(a.task_image_path));
      imgWrap.appendChild(pathLine);
      const img = document.createElement("img");
      // Kid task images are served by httpx.MountImages at /storage/images/<rel>.
      img.src = `/storage/images/${a.task_image_path}`;
      img.alt = "task image";
      img.setAttribute("style", "max-width: 200px; max-height: 200px; border: 1px solid #e5e7eb; border-radius: 4px;");
      imgWrap.appendChild(img);
      cardBody.appendChild(imgWrap);
    }

    // ── STT / LLM detail ──────────────────────────────────────────────
    const dBody = section(body, "STT / LLM detail");
    const transcriptPre = document.createElement("pre");
    transcriptPre.textContent = a.user_speech_raw || "(empty)";
    transcriptPre.setAttribute("style", "white-space: pre-wrap; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 8px; font-size: 13px; margin: 0 0 12px 0;");
    const transcriptLabel = document.createElement("div");
    transcriptLabel.textContent = "Transcript (STT raw):";
    transcriptLabel.setAttribute("style", "font-size: 12px; color: #6b7280; margin-bottom: 4px;");
    dBody.appendChild(transcriptLabel);
    dBody.appendChild(transcriptPre);
    dBody.appendChild(kvList([
      ["LLM attempts", String(a.llm_attempts)],
      ["LLM tokens (input / cached / output)", `${numOrDash(a.llm_input_tokens)} / ${numOrDash(a.llm_cached_input_tokens)} / ${numOrDash(a.llm_output_tokens)}`],
      ["LLM cost", code(fmtMicrousd(a.llm_cost_microusd))],
      ["STT provider / model", `${a.stt_provider ?? "—"} / ${a.stt_model ?? "—"}`],
      ["STT usage kind", a.stt_usage_kind ?? "—"],
      ["STT tokens (input / output)", `${numOrDash(a.stt_input_tokens)} / ${numOrDash(a.stt_output_tokens)}`],
      ["STT audio duration", a.stt_audio_ms != null ? `${a.stt_audio_ms} ms` : "—"],
      ["STT cost", code(fmtMicrousd(a.stt_cost_microusd))],
      ["STT presets", a.stt_presets.length ? a.stt_presets.join(", ") : "—"],
    ]));

    const note = document.createElement("p");
    note.textContent = "Per-call request & response payloads are captured only when config.llm_logging_enabled was on at turn time. STT request bodies have the audio bytes redacted to a sha256 placeholder.";
    note.setAttribute("style", "font-size: 12px; color: #6b7280; margin: 12px 0 8px 0;");
    dBody.appendChild(note);

    const btnRow = document.createElement("div");
    btnRow.setAttribute("style", "display: flex; gap: 8px;");
    const llmPanel = document.createElement("div");
    llmPanel.setAttribute("style", "margin-top: 12px;");
    const sttPanel = document.createElement("div");
    sttPanel.setAttribute("style", "margin-top: 12px;");
    btnRow.appendChild(toggleButton("Show LLM calls", llmPanel, () => loadLLMCalls(a.id, llmPanel)));
    btnRow.appendChild(toggleButton("Show STT calls", sttPanel, () => loadSTTCalls(a.id, sttPanel)));
    dBody.appendChild(btnRow);
    dBody.appendChild(llmPanel);
    dBody.appendChild(sttPanel);

    // ── Captured audio ────────────────────────────────────────────────
    const audioBody = section(body, "Captured audio");
    if (a.has_audio) {
      const player = document.createElement("audio");
      player.controls = true;
      player.preload = "none";
      player.src = `/api/admin/attempts/${encodeURIComponent(a.id)}/audio`;
      player.setAttribute("style", "width: 100%; max-width: 420px;");
      audioBody.appendChild(player);
    } else {
      const off = document.createElement("p");
      off.textContent = "No captured audio for this attempt (config.retention_enabled was off at turn time).";
      off.setAttribute("style", "color: #6b7280; font-size: 14px; margin: 0;");
      audioBody.appendChild(off);
    }
  }

  async function load(): Promise<void> {
    try {
      const a = await adminGet<AdminAttemptDetailDTO>(`/api/admin/attempts/${encodeURIComponent(attemptID)}`);
      render(a);
    } catch (err) {
      if (err instanceof AdminError && err.status === 404) {
        status.textContent = `Attempt ${attemptID} not found.`;
        return;
      }
      status.textContent = errMessage(err, "load attempt");
    }
  }

  void load();
  return () => {};
}

// ── Per-call panels ──────────────────────────────────────────────────────

async function loadLLMCalls(attemptID: string, panel: HTMLElement): Promise<void> {
  panel.replaceChildren(loadingNote());
  try {
    const calls = await adminGet<AdminLLMCallDTO[]>(`/api/admin/attempts/${encodeURIComponent(attemptID)}/llm-calls`);
    panel.replaceChildren();
    if (calls.length === 0) {
      panel.appendChild(emptyCallsNote("No captured LLM calls — config.llm_logging_enabled was off at turn time, or every call was guard-short-circuited."));
      return;
    }
    for (const c of calls) {
      panel.appendChild(callBlock(
        `Call #${c.sequence}`, c.status, fmtTimestamp(c.created_at),
        [
          ["Model", code(c.model)],
          ["Latency", `${c.latency_ms} ms`],
          ["Tokens (input / cached / output)", `${c.input_tokens} / ${c.cached_input_tokens} / ${c.output_tokens}`],
          ["Cost", code(fmtMicrousd(c.cost_microusd))],
          ["Failure class", c.failure_class ? code(c.failure_class) : "—"],
          ["Error", c.error_text ? code(c.error_text) : "—"],
        ],
        c.payload_json, c.read_err,
      ));
    }
  } catch (err) {
    panel.replaceChildren(emptyCallsNote(errMessage(err, "load LLM calls")));
  }
}

async function loadSTTCalls(attemptID: string, panel: HTMLElement): Promise<void> {
  panel.replaceChildren(loadingNote());
  try {
    const calls = await adminGet<AdminSTTCallDTO[]>(`/api/admin/attempts/${encodeURIComponent(attemptID)}/stt-calls`);
    panel.replaceChildren();
    if (calls.length === 0) {
      panel.appendChild(emptyCallsNote("No captured STT calls — config.llm_logging_enabled was off at turn time."));
      return;
    }
    for (const c of calls) {
      panel.appendChild(callBlock(
        `Call #${c.sequence}`, c.status, fmtTimestamp(c.created_at),
        [
          ["Provider / model", `${c.provider} / ${c.model}`],
          ["Latency", `${c.latency_ms} ms`],
          ["MIME", code(c.mime_type)],
          ["Audio bytes / sha256", appendInline(document.createTextNode(`${c.audio_bytes} · `), code(c.audio_sha256 || "—"))],
          ["Usage kind", code(c.usage_kind || "—")],
          ["Tokens (input / output)", `${c.input_tokens} / ${c.output_tokens}`],
          ["Audio duration", `${c.audio_ms} ms`],
          ["Cost", code(fmtMicrousd(c.cost_microusd))],
          ["Error", c.error_text ? code(c.error_text) : "—"],
        ],
        c.payload_json, c.read_err,
      ));
    }
  } catch (err) {
    panel.replaceChildren(emptyCallsNote(errMessage(err, "load STT calls")));
  }
}

// ── Small DOM helpers ────────────────────────────────────────────────────

function numOrDash(n: number | null | undefined): string {
  return n == null ? "—" : String(n);
}

function appendInline(...nodes: Node[]): HTMLSpanElement {
  const span = document.createElement("span");
  for (const n of nodes) span.appendChild(n);
  return span;
}

function loadingNote(): HTMLParagraphElement {
  const p = document.createElement("p");
  p.textContent = "Loading…";
  p.setAttribute("style", "color: #6b7280; font-size: 13px; margin: 8px 0;");
  return p;
}

function emptyCallsNote(text: string): HTMLParagraphElement {
  const p = document.createElement("p");
  p.textContent = text;
  p.setAttribute("style", "color: #6b7280; font-size: 13px; margin: 8px 0;");
  return p;
}

// toggleButton flips the visibility of `panel` on click; the first time it's
// shown it calls `onFirstShow` (which loads the data into `panel`).
function toggleButton(label: string, panel: HTMLElement, onFirstShow: () => void): HTMLButtonElement {
  panel.style.display = "none";
  let loaded = false;
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = label;
  b.setAttribute("style", "padding: 6px 12px; background: white; color: #1d4ed8; border: 1px solid #d1d5db; border-radius: 4px; cursor: pointer; font-size: 13px;");
  b.addEventListener("click", () => {
    const showing = panel.style.display !== "none";
    if (showing) {
      panel.style.display = "none";
      b.textContent = label;
      return;
    }
    panel.style.display = "block";
    b.textContent = label.replace("Show", "Hide");
    if (!loaded) {
      loaded = true;
      onFirstShow();
    }
  });
  return b;
}

// callBlock builds a <details>-style block for one captured call: a header
// (title + status pill + time), a kv grid, and a collapsible <pre> with the
// payload JSON (or the read_err in red).
function callBlock(title: string, statusStr: string, time: string, kv: Array<[string, string | Node]>, payloadJSON: string | null, readErr: string | null): HTMLElement {
  const wrap = document.createElement("div");
  wrap.setAttribute("style", "border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; margin-bottom: 8px; background: #fafafa;");

  const header = document.createElement("div");
  header.setAttribute("style", "display: flex; gap: 8px; align-items: center; margin-bottom: 8px;");
  const t = document.createElement("strong");
  t.textContent = title;
  t.setAttribute("style", "font-size: 14px;");
  header.appendChild(t);
  header.appendChild(statusStr === "success" ? pill("success", "#166534", "#dcfce7") : pill("failure", "#991b1b", "#fee2e2"));
  const ts = document.createElement("span");
  ts.textContent = time;
  ts.setAttribute("style", "font-size: 12px; color: #6b7280;");
  header.appendChild(ts);
  wrap.appendChild(header);

  wrap.appendChild(kvList(kv));

  if (readErr) {
    const e = document.createElement("p");
    e.textContent = `Payload unavailable: ${readErr}`;
    e.setAttribute("style", "color: #991b1b; font-size: 13px; margin: 8px 0 0 0;");
    wrap.appendChild(e);
  } else if (payloadJSON != null) {
    const details = document.createElement("details");
    const summary = document.createElement("summary");
    summary.textContent = "Payload (request + response)";
    summary.setAttribute("style", "cursor: pointer; font-size: 13px; color: #4b5563; margin-top: 8px;");
    details.appendChild(summary);
    const pre = document.createElement("pre");
    pre.textContent = prettyJSON(payloadJSON);
    pre.setAttribute("style", "white-space: pre-wrap; max-height: 480px; overflow: auto; background: white; border: 1px solid #e5e7eb; border-radius: 4px; padding: 8px; font-size: 12px; margin: 8px 0 0 0;");
    details.appendChild(pre);
    wrap.appendChild(details);
  }
  return wrap;
}

// prettyJSON re-indents the payload file if it's valid JSON; otherwise it
// returns the raw text untouched (the file is captured verbatim, so it
// should already be JSON, but be defensive).
function prettyJSON(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
